import { ExternalLink, Mail, MailOpen } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton, SkeletonRows } from '@/components/ui/skeleton'
import { ADMIN_BASE_URL, adminHref } from '../../../../config/config'
import { useApplicationEmails } from '../../../../models/internalApplication'
import { EmailDirection, type EmailMessage } from '../../../../models/lossRunRequest'

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function EmailListItem({
  email,
  isSelected,
  onSelect,
}: {
  email: EmailMessage
  isSelected: boolean
  onSelect: () => void
}) {
  const isOutbound = email.direction === EmailDirection.OUTBOUND
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
        isSelected ? 'border-blue-500 bg-blue-50' : 'border-border bg-surface hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-2">
        {isOutbound ? (
          <Mail className="text-muted-foreground size-4 shrink-0" />
        ) : (
          <MailOpen className="text-muted-foreground size-4 shrink-0" />
        )}
        <Badge variant={isOutbound ? 'outline' : 'secondary'} className="text-xs">
          {isOutbound ? 'Sent' : 'Received'}
        </Badge>
      </div>
      <div className="mt-1 truncate font-medium">{email.subject || '(no subject)'}</div>
      <div className="text-muted-foreground mt-1 truncate text-xs">
        {isOutbound ? `To ${email.toEmail}` : `From ${email.fromEmail}`}
      </div>
      <div className="text-muted-foreground mt-1 text-xs">{formatDateTime(email.dateCreated)}</div>
    </button>
  )
}

function EmailContent({ email }: { email: EmailMessage }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="wrap-break-word">{email.subject || '(no subject)'}</CardTitle>
        <div className="text-muted-foreground mt-1 flex flex-col gap-0.5 text-sm">
          <div>
            <span className="font-medium">From:</span> {email.fromEmail}
          </div>
          <div>
            <span className="font-medium">To:</span> {email.toEmail}
          </div>
          <div>
            <span className="font-medium">Date:</span> {formatDateTime(email.dateCreated)}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {email.bodyHtml ? (
          <iframe
            key={email.id}
            srcDoc={email.bodyHtml}
            title={email.subject || 'Email'}
            className="w-full rounded border-0"
            style={{ minHeight: 200 }}
            onLoad={(e) => {
              const frame = e.currentTarget
              if (frame.contentDocument) {
                frame.contentDocument.body.style.fontFamily =
                  '"Google Sans",Roboto,RobotoDraft,Helvetica,Arial,sans-serif'
                frame.style.height = `${frame.contentDocument.documentElement.scrollHeight}px`
              }
            }}
            sandbox="allow-same-origin"
          />
        ) : (
          <div className="text-sm whitespace-pre-line">{email.bodyText}</div>
        )}
        <div className="mt-4 flex items-center gap-3">
          <a
            href={adminHref(`${ADMIN_BASE_URL}/insure/emailmessage/${email.id}/change/`)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
          >
            Email <ExternalLink className="size-3" />
          </a>
          {email.agentRunId && (
            <a
              href={adminHref(`${ADMIN_BASE_URL}/insure/agentrun/${email.agentRunId}/change/`)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
            >
              Agent Run <ExternalLink className="size-3" />
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function EmailsTab({ applicationId }: { applicationId: string }) {
  const { emails, isLoading } = useApplicationEmails(applicationId)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const sortedDesc = useMemo(() => [...emails].reverse(), [emails])
  const lastEmail = emails.at(-1) ?? null

  useEffect(() => {
    if (lastEmail && (!selectedId || !emails.some((e) => e.id === selectedId))) {
      setSelectedId(lastEmail.id)
    }
  }, [emails, lastEmail, selectedId])

  if (isLoading) {
    return (
      <div className="mt-4 grid grid-cols-[280px_1fr] gap-6">
        <SkeletonRows rows={5} rowClassName="h-16" />
        <Skeleton className="h-80 w-full" />
      </div>
    )
  }

  if (emails.length === 0) {
    return <p className="text-muted-foreground mt-4 text-sm">No emails yet.</p>
  }

  const selected = emails.find((e) => e.id === selectedId) ?? lastEmail

  return (
    <div className="mt-4 grid grid-cols-[280px_1fr] gap-6">
      <div className="flex flex-col gap-1">
        {sortedDesc.map((email) => (
          <EmailListItem
            key={email.id}
            email={email}
            isSelected={selected?.id === email.id}
            onSelect={() => setSelectedId(email.id)}
          />
        ))}
      </div>
      <div className="flex min-w-0 flex-col gap-4">{selected && <EmailContent email={selected} />}</div>
    </div>
  )
}
